/**
 * LogRow - one row in the AdminLogsPage table (Phase 12.3).
 *
 * Columns: timestamp, level badge, service, action, message, trace_id.
 * The message column is truncated by default and expands on click; the
 * trace_id is rendered monospace and copies to the clipboard when its
 * inline "Copy" button is pressed.
 */
import { useCallback, useState } from "react";

import { LevelBadge } from "./LevelBadge";
import type { SystemLogResponse } from "@/types/api";

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString();
}

export interface LogRowProps {
  row: SystemLogResponse;
}

export function LogRow({ row }: LogRowProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const handleCopyTrace = useCallback(() => {
    if (!row.trace_id) return;
    try {
      void navigator.clipboard?.writeText(row.trace_id);
    } catch {
      /* clipboard unavailable - silently no-op. */
    }
  }, [row.trace_id]);

  return (
    <tr className="hover:bg-neutral-50">
      <td className="whitespace-nowrap px-3 py-2 text-xs text-neutral-500">
        {formatTimestamp(row.timestamp)}
      </td>
      <td className="px-3 py-2 text-xs">
        <LevelBadge level={row.level} />
      </td>
      <td className="px-3 py-2 text-xs font-medium text-neutral-700">
        {row.service}
      </td>
      <td className="px-3 py-2 text-xs text-neutral-700">{row.action}</td>
      <td
        className="px-3 py-2 text-xs text-neutral-700"
        onClick={() => setExpanded((v) => !v)}
      >
        <button
          type="button"
          className={`block w-full text-left ${
            expanded ? "whitespace-pre-wrap" : "truncate"
          }`}
          aria-expanded={expanded}
        >
          {row.message}
        </button>
        {expanded && row.error_trace && (
          <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-neutral-100 p-2 text-[11px] text-neutral-700">
            {row.error_trace}
          </pre>
        )}
        {expanded && Object.keys(row.context ?? {}).length > 0 && (
          <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-neutral-100 p-2 text-[11px] text-neutral-700">
            {JSON.stringify(row.context, null, 2)}
          </pre>
        )}
      </td>
      <td className="px-3 py-2 font-mono text-[11px] text-neutral-500">
        {row.trace_id ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="truncate" title={row.trace_id}>
              {row.trace_id.slice(0, 8)}
            </span>
            <button
              type="button"
              onClick={handleCopyTrace}
              className="rounded p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700"
              aria-label="Copy trace id"
              title="Copy trace id"
            >
              <i
                className="fa-duotone fa-thin fa-copy text-sm"
                aria-hidden="true"
              />
            </button>
          </span>
        ) : (
          <span className="text-neutral-300">-</span>
        )}
      </td>
    </tr>
  );
}

export default LogRow;
